/**
 * Handles data transformation for deliverybook feature
 */
import constants from '@core/utils/constants';

/**
 * Serialize address for API request
 */
function serializeAddress(addressData) {
  return {
    label: addressData.name || addressData.label || '', // The UI historically uses 'name' for the label
    formattedAddress:
      addressData.address ||
      addressData.locationText ||
      addressData.location?.address ||
      '',
    latitude: addressData.lat || addressData.location?.lat || 0,
    longitude: addressData.lng || addressData.location?.lng || 0,
    locationDescription:
      addressData.description ||
      addressData.instructions ||
      addressData.location?.description ||
      '',
    country: addressData.country || '',
    city: addressData.city || addressData.location?.city || '',
    state: addressData.state || '',
    district: addressData.district || '',
    street: addressData.street || '',
    houseNumber: addressData.houseNumber || '',
    source: addressData.source || 'user_input',
    accuracy: addressData.accuracy || 10,
    isDefault: addressData.isDefault || false,
    userId: addressData.userId || getUserId()
  };
}

/**
 * Serialize address request for API
 */
function serializeAddressRequest(requestData) {
  return {
    name: requestData.name || '',
    phoneNumber: requestData.phoneNumber || '',
    senderId: requestData.userId || getUserId()
  };
}

/**
 * Serialize address invite for API
 */
function serializeAddressInvite(inviteData) {
  return {
    name: inviteData.name || '',
    phoneNumber: inviteData.phoneNumber || inviteData.phone || '',
    userId: inviteData.userId || getUserId()
  };
}

/**
 * Deserialize address from API response (V2 and V3 formats)
 * @param {Object} addressDoc - Raw address document from API
 * @param {'saved'|'invitation'|'request'} [sourceType='saved'] - Source list type to set correct links
 */
function deserializeAddress(addressDoc, sourceType = 'saved') {
  if (!addressDoc) return null;

  // Handle both address as subdocument and standalone format
  const location = addressDoc.location || addressDoc.coordinates || {};

  // Handle V2 format with coordinates array [lng, lat] and V3 latitude/longitude
  let lat = addressDoc.latitude || addressDoc.lat || location.lat || 0;
  let lng = addressDoc.longitude || addressDoc.lng || location.lng || 0;

  if (
    Array.isArray(location.coordinates) &&
    location.coordinates.length === 2
  ) {
    lng = location.coordinates[0];
    lat = location.coordinates[1];
  }

  const invitationBaseUrl = constants?.URLS?.invitationBaseUrl;
  const locationRequestBaseUrl = constants?.URLS?.locationRequestBaseUrl;
  const id =
    addressDoc.id || addressDoc._id || `address-${Date.now()}-${Math.random()}`;

  // Extraire les champs spécifiques à Location API
  const formattedAddress =
    addressDoc.formattedAddress ||
    location.address ||
    addressDoc.address ||
    addressDoc.locationText ||
    '';
  const nameLabel = addressDoc.label || addressDoc.name || '';
  const description =
    addressDoc.locationDescription ||
    location.description ||
    addressDoc.description ||
    '';
  const city = addressDoc.city || location.city || '';

  // Construire les liens uniquement selon le type réel de l'item
  // pour éviter de confondre le controller lors de la suppression
  const invitationLink =
    sourceType === 'invitation'
      ? `${invitationBaseUrl}/register?ref=${id}`
      : null;

  const locationRequestLink =
    sourceType === 'request'
      ? `${locationRequestBaseUrl}/request-location/${id}`
      : null;

  return {
    id: id,
    name: nameLabel, // Used by the UI as label/name
    phoneNumber: addressDoc.phoneNumber || addressDoc.phone || '',
    pseudo: addressDoc.pseudo || '',
    locationText: formattedAddress,
    location: {
      lat: lat,
      lng: lng,
      address: formattedAddress,
      city: city,
      description: description
    },
    isDefault: addressDoc.isDefault || false,
    isRapidooUser: addressDoc.isRapidooUser || false,
    sourceType: sourceType, // Conservé pour usage interne (suppression, affichage)
    invitationLink: invitationLink,
    locationRequestLink: locationRequestLink,
    status: addressDoc.status || 'pending',
    createdAt: addressDoc.createdAt || new Date().toISOString(),
    updatedAt: addressDoc.updatedAt || new Date().toISOString()
  };
}

/**
 * Deserialize addresses list from API response
 * @param {Object} response - Raw API response
 * @param {'saved'|'invitation'|'request'} [sourceType='saved'] - Source list type
 */
function deserializeAddressesList(response, sourceType = 'saved') {
  if (!response) {
    return {
      success: false,
      addresses: [],
      message: 'No data received'
    };
  }

  // Handle V2 style response
  if (response.statusCode !== undefined) {
    if (response.statusCode !== 200) {
      return {
        success: false,
        addresses: [],
        message: response.message || 'Error loading addresses'
      };
    }
  }

  // Handle different data formats
  const addressesData = response.data?.data || response.data || [];

  return {
    success: true,
    addresses: Array.isArray(addressesData)
      ? addressesData
          .map((item) => deserializeAddress(item, sourceType))
          .filter(Boolean)
      : [deserializeAddress(addressesData, sourceType)].filter(Boolean),
    message: response.message || 'Addresses loaded successfully'
  };
}

/**
 * Deserialize create/update response
 * @param {Object} response - Raw API response
 * @param {'saved'|'invitation'|'request'} [sourceType='saved'] - Source list type
 */
function deserializeAddressResponse(response, sourceType = 'saved') {
  if (!response) {
    return {
      success: false,
      address: null,
      invite: null,
      request: null,
      message: 'Operation failed'
    };
  }

  // Handle V2 style response
  if (response.statusCode !== undefined) {
    const success = response.statusCode === 200 || response.statusCode === 201;
    const addressData = response.data;

    const deserialized = success
      ? deserializeAddress(addressData, sourceType)
      : null;

    return {
      success,
      address: deserialized,
      invite: sourceType === 'invitation' ? deserialized : null,
      request: sourceType === 'request' ? deserialized : null,
      message:
        response.message ||
        (success ? 'Operation successful' : 'Operation failed')
    };
  }

  // Handle V3 style response
  const addressData = response.data?.address || response.data;
  const deserialized = deserializeAddress(addressData, sourceType);

  return {
    success: true,
    address: deserialized,
    invite: sourceType === 'invitation' ? deserialized : null,
    request: sourceType === 'request' ? deserialized : null,
    message: response.message || 'Operation successful'
  };
}

/**
 * Deserialize error response
 */
function deserializeError(response) {
  const errorData = response?.data?.error || response?.error || response;

  return {
    message: errorData?.message || response?.message || 'An error occurred',
    code: errorData?.code || response?.code || 'DELIVERYBOOK_ERROR',
    details: errorData?.details || null
  };
}

/**
 * Get current userId from user store
 */
function getUserId() {
  const userStore = window.$stores?.user;
  return userStore?.state?.current?.id || userStore?.state?.userId || null;
}

/**
 * Get current referral code from user store
 */
function getReferralCode() {
  const userStore = window.$stores?.user;
  return userStore?.state?.current?.referralCode || '';
}

export default {
  serializeAddress,
  serializeAddressRequest,
  serializeAddressInvite,
  deserializeAddress,
  deserializeAddressesList,
  deserializeAddressResponse,
  deserializeError,
  getUserId
};
